import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  formatCurrency,
  formatCaneRate,
  formatTonRate,
  formatDate,
  getStatusColor,
} from '../../lib/utils';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Edit,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Users,
  Truck,
  DollarSign,
  UserCircle,
  X,
  Printer,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PayrollDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [period, setPeriod] = useState(null);
  const [dayEntries, setDayEntries] = useState([]);
  const [weeklyPayroll, setWeeklyPayroll] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [truckers, setTruckers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit Day Entry Dialog (only workers_rate and tons)
  const [editDayDialogOpen, setEditDayDialogOpen] = useState(false);
  const [editDayData, setEditDayData] = useState(null);

  // Add Daily Delivery Dialog
  const [addDayDialogOpen, setAddDayDialogOpen] = useState(false);
  const [newDayData, setNewDayData] = useState({
    date: '',
    cane_rate: '',
    workers_rate: '1',
    tons: '1',
    owner_id: '',
  });

  // Worker Entry Dialog - Now supports multiple workers
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [selectedDayEntry, setSelectedDayEntry] = useState(null);
  const [selectedWorkerEntry, setSelectedWorkerEntry] = useState(null);
  const [isEditingWorker, setIsEditingWorker] = useState(false);
  // Multiple workers state
  const [workersList, setWorkersList] = useState([{ worker_id: '', tons_cut: '', notes: '' }]);

  // Trucker Dialog
  const [truckerDialogOpen, setTruckerDialogOpen] = useState(false);
  const [selectedDayForTrucker, setSelectedDayForTrucker] = useState(null);
  const [truckerFormData, setTruckerFormData] = useState({
    trucker_id: '',
    notes: '',
  });

  // Collapsible states
  const [expandedTruckers, setExpandedTruckers] = useState({});
  const [expandedOwners, setExpandedOwners] = useState({});
  const [expandedWorkers, setExpandedWorkers] = useState({});
  const [expandedSummary, setExpandedSummary] = useState({});
  const [expandedGrabmen, setExpandedGrabmen] = useState({});
  const [expandedDrivers, setExpandedDrivers] = useState({});
  
  // Data for each day entry
  const [dayWorkers, setDayWorkers] = useState({});
  const [dayTruckers, setDayTruckers] = useState({});
  const [dayOwners, setDayOwners] = useState({});
  const [dayGrabmen, setDayGrabmen] = useState({});
  const [dayDrivers, setDayDrivers] = useState({});

  // Group Leader Summary
  const [groupLeaderSummary, setGroupLeaderSummary] = useState(null);

  // Grabman & Driver Summaries
  const [grabmanSummary, setGrabmanSummary] = useState(null);
  const [driverSummary, setDriverSummary] = useState(null);

  // Grabman Dialog
  const [grabmanDialogOpen, setGrabmanDialogOpen] = useState(false);
  const [selectedDayForGrabman, setSelectedDayForGrabman] = useState(null);
  const [grabmanFormData, setGrabmanFormData] = useState({ worker_id: '', notes: '' });

  // Driver Dialog
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [selectedDayForDriver, setSelectedDayForDriver] = useState(null);
  const [driverFormData, setDriverFormData] = useState({ worker_id: '', notes: '' });

  // Extra Deductibles Dialog
  const [extraDeductiblesDialogOpen, setExtraDeductiblesDialogOpen] = useState(false);
  const [extraDeductibles, setExtraDeductibles] = useState([]);

  const [saving, setSaving] = useState(false);

  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [changingStatus, setChangingStatus] = useState(false);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Tab state - "summary" or a date string
  const [activeTab, setActiveTab] = useState('summary');

  // Load all data including expanded sections data
  const fetchPeriodData = useCallback(async () => {
    try {
      setLoading(true);
      const [periodRes, dayEntriesRes, workersRes, truckersRes, ownersRes, weeklyRes, groupLeaderRes, grabmanSummaryRes, driverSummaryRes] = await Promise.all([
        axios.get(`${API_URL}/payroll-periods/${id}`),
        axios.get(`${API_URL}/payroll-periods/${id}/day-entries`),
        axios.get(`${API_URL}/workers?is_active=true`),
        axios.get(`${API_URL}/truckers?is_active=true`),
        axios.get(`${API_URL}/owners?is_active=true`),
        axios.get(`${API_URL}/payroll-periods/${id}/weekly-payroll`),
        axios.get(`${API_URL}/payroll-periods/${id}/group-leader-summary`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/payroll-periods/${id}/grabman-summary`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/payroll-periods/${id}/driver-summary`).catch(() => ({ data: null })),
      ]);

      setPeriod(periodRes.data);
      setDayEntries(dayEntriesRes.data);
      setWorkers(workersRes.data);
      setTruckers(truckersRes.data);
      setOwners(ownersRes.data);
      setWeeklyPayroll(weeklyRes.data);
      setGroupLeaderSummary(groupLeaderRes.data);
      setGrabmanSummary(grabmanSummaryRes.data);
      setDriverSummary(driverSummaryRes.data);
      setExtraDeductibles(periodRes.data.extra_deductibles || []);

      // Auto-load all day entry data for truckers, owners, workers, grabmen, and drivers
      const dayIds = dayEntriesRes.data.map(d => d.id);
      const loadPromises = [];
      
      for (const dayId of dayIds) {
        loadPromises.push(
          axios.get(`${API_URL}/day-entries/${dayId}/truckers`)
            .then(res => ({ dayId, type: 'truckers', data: res.data }))
            .catch(() => ({ dayId, type: 'truckers', data: [] }))
        );
        loadPromises.push(
          axios.get(`${API_URL}/day-entries/${dayId}/owners`)
            .then(res => ({ dayId, type: 'owners', data: res.data }))
            .catch(() => ({ dayId, type: 'owners', data: [] }))
        );
        loadPromises.push(
          axios.get(`${API_URL}/day-entries/${dayId}/workers`)
            .then(res => ({ dayId, type: 'workers', data: res.data }))
            .catch(() => ({ dayId, type: 'workers', data: [] }))
        );
        loadPromises.push(
          axios.get(`${API_URL}/day-entries/${dayId}/grabmen`)
            .then(res => ({ dayId, type: 'grabmen', data: res.data }))
            .catch(() => ({ dayId, type: 'grabmen', data: [] }))
        );
        loadPromises.push(
          axios.get(`${API_URL}/day-entries/${dayId}/drivers`)
            .then(res => ({ dayId, type: 'drivers', data: res.data }))
            .catch(() => ({ dayId, type: 'drivers', data: [] }))
        );
      }
      
      if (loadPromises.length > 0) {
        const results = await Promise.all(loadPromises);
        const newTruckers = {};
        const newOwners = {};
        const newWorkers = {};
        const newGrabmen = {};
        const newDrivers = {};
        
        results.forEach(({ dayId, type, data }) => {
          if (type === 'truckers') newTruckers[dayId] = data;
          if (type === 'owners') newOwners[dayId] = data;
          if (type === 'workers') newWorkers[dayId] = data;
          if (type === 'grabmen') newGrabmen[dayId] = data;
          if (type === 'drivers') newDrivers[dayId] = data;
        });
        
        setDayTruckers(newTruckers);
        setDayOwners(newOwners);
        setDayWorkers(newWorkers);
        setDayGrabmen(newGrabmen);
        setDayDrivers(newDrivers);
      }
    } catch (error) {
      toast.error('Failed to load payroll data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPeriodData();
    }
  }, [id, fetchPeriodData]);

  // Status change
  const statusTransitions = {
    draft: { next: 'reviewed', nextLabel: 'Mark as Reviewed', nextDesc: 'Submit this payroll for review.' },
    reviewed: { next: 'approved', nextLabel: 'Approve', nextDesc: 'Approve this payroll for payment.', prev: 'draft', prevLabel: 'Back to Draft' },
    approved: { next: 'paid', nextLabel: 'Mark as Paid', nextDesc: 'Confirm payment has been made.', prev: 'reviewed', prevLabel: 'Back to Reviewed' },
    paid: { next: 'reversed', nextLabel: 'Reverse', nextDesc: 'Reverse this payroll. This cannot be undone.' },
  };

  const handleStatusChange = async () => {
    if (!pendingStatus) return;
    setChangingStatus(true);
    try {
      await axios.put(`${API_URL}/payroll-periods/${id}/status?status=${pendingStatus}`);
      toast.success(`Status changed to ${pendingStatus}`);
      setStatusDialogOpen(false);
      setPendingStatus(null);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };

  // Edit Day Entry (workers_rate and tons only)
  const handleOpenEditDayDialog = (dayEntry) => {
    setEditDayData({
      id: dayEntry.id,
      date: dayEntry.date,
      cane_rate: dayEntry.cane_rate,
      workers_rate: dayEntry.workers_rate.toString(),
      tons: dayEntry.tons.toString(),
    });
    setEditDayDialogOpen(true);
  };

  const handleUpdateDayEntry = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/day-entries/${editDayData.id}`, {
        workers_rate: parseFloat(editDayData.workers_rate),
        tons: parseFloat(editDayData.tons),
      });

      toast.success('Day entry updated');
      setEditDayDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update day entry');
    } finally {
      setSaving(false);
    }
  };

  // Add Daily Delivery
  const handleOpenAddDayDialog = () => {
    setNewDayData({
      date: '',
      cane_rate: '',
      workers_rate: '1',
      tons: '1',
      owner_id: '',
    });
    setAddDayDialogOpen(true);
  };

  const handleCreateDayEntry = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // First create the day entry
      const dayResponse = await axios.post(`${API_URL}/payroll-periods/${id}/day-entries`, {
        date: newDayData.date,
        cane_rate: parseFloat(newDayData.cane_rate),
        workers_rate: parseFloat(newDayData.workers_rate),
        tons: parseFloat(newDayData.tons),
      });

      // If owner is selected (not "none"), add owner to the day entry
      if (newDayData.owner_id && newDayData.owner_id !== 'none') {
        await axios.post(`${API_URL}/day-entries/${dayResponse.data.id}/owners`, {
          owner_id: newDayData.owner_id,
        });
      }

      toast.success('Daily delivery created successfully');
      setAddDayDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create daily delivery');
    } finally {
      setSaving(false);
    }
  };

  // Worker handlers - Now with multiple workers support
  const handleOpenWorkerDialog = (dayEntry, workerEntry = null) => {
    setSelectedDayEntry(dayEntry);

    if (workerEntry) {
      // Editing single worker
      setIsEditingWorker(true);
      setSelectedWorkerEntry(workerEntry);
      setWorkersList([{
        worker_id: workerEntry.worker_id,
        tons_cut: workerEntry.tons_cut.toString(),
        notes: workerEntry.notes || '',
      }]);
    } else {
      // Adding new workers (multiple)
      setIsEditingWorker(false);
      setSelectedWorkerEntry(null);
      setWorkersList([{ worker_id: '', tons_cut: '', notes: '' }]);
    }
    setWorkerDialogOpen(true);
  };

  const handleAddWorkerRow = () => {
    setWorkersList(prev => [...prev, { worker_id: '', tons_cut: '', notes: '' }]);
  };

  const handleRemoveWorkerRow = (index) => {
    setWorkersList(prev => prev.filter((_, i) => i !== index));
  };

  const handleWorkerChange = (index, field, value) => {
    setWorkersList(prev => prev.map((w, i) => 
      i === index ? { ...w, [field]: value } : w
    ));
  };

  const handleSaveWorkers = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditingWorker) {
        // Update single worker
        await axios.put(`${API_URL}/worker-day-entries/${selectedWorkerEntry.id}`, {
          tons_cut: parseFloat(workersList[0].tons_cut),
          notes: workersList[0].notes,
        });
        toast.success('Worker updated successfully');
      } else {
        // Add multiple workers
        const validWorkers = workersList.filter(w => w.worker_id && w.tons_cut);
        if (validWorkers.length === 0) {
          toast.error('Please add at least one worker with grabs cut');
          setSaving(false);
          return;
        }

        // Use bulk endpoint
        await axios.post(`${API_URL}/day-entries/${selectedDayEntry.id}/workers/bulk`, {
          workers: validWorkers.map(w => ({
            worker_id: w.worker_id,
            tons_cut: parseFloat(w.tons_cut),
            notes: w.notes || null,
          })),
        });
        toast.success(`${validWorkers.length} worker(s) added successfully`);
      }

      setWorkerDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditingWorker ? 'update' : 'add'} worker(s)`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorker = (dayEntryId, workerEntryId) => {
    setDeleteTarget({ type: 'worker', id: workerEntryId, label: 'worker' });
    setDeleteDialogOpen(true);
  };

  // Trucker handlers
  const handleOpenTruckerDialog = (dayEntry) => {
    setSelectedDayForTrucker(dayEntry);
    setTruckerFormData({ trucker_id: '', notes: '' });
    setTruckerDialogOpen(true);
  };

  const handleAddTrucker = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/day-entries/${selectedDayForTrucker.id}/truckers`, {
        trucker_id: truckerFormData.trucker_id,
        notes: truckerFormData.notes,
      });

      toast.success('Trucker added successfully');
      setTruckerDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add trucker');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrucker = (dayEntryId, truckerEntryId) => {
    setDeleteTarget({ type: 'trucker', id: truckerEntryId, label: 'trucker' });
    setDeleteDialogOpen(true);
  };

  // Grabman handlers
  const handleOpenGrabmanDialog = (dayEntry) => {
    setSelectedDayForGrabman(dayEntry);
    setGrabmanFormData({ worker_id: '', notes: '' });
    setGrabmanDialogOpen(true);
  };

  const handleAddGrabman = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/day-entries/${selectedDayForGrabman.id}/grabmen`, {
        worker_id: grabmanFormData.worker_id,
        notes: grabmanFormData.notes,
      });

      toast.success('Grabman added successfully');
      setGrabmanDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add grabman');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGrabman = (grabmanEntryId) => {
    setDeleteTarget({ type: 'grabman', id: grabmanEntryId, label: 'grabman' });
    setDeleteDialogOpen(true);
  };

  // Driver handlers
  const handleOpenDriverDialog = (dayEntry) => {
    setSelectedDayForDriver(dayEntry);
    setDriverFormData({ worker_id: '', notes: '' });
    setDriverDialogOpen(true);
  };

  const handleAddDriver = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API_URL}/day-entries/${selectedDayForDriver.id}/drivers`, {
        worker_id: driverFormData.worker_id,
        notes: driverFormData.notes,
      });

      toast.success('Driver added successfully');
      setDriverDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add driver');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDriver = (driverEntryId) => {
    setDeleteTarget({ type: 'driver', id: driverEntryId, label: 'driver' });
    setDeleteDialogOpen(true);
  };

  // Extra Deductibles handlers
  const handleOpenExtraDeductiblesDialog = () => {
    setExtraDeductiblesDialogOpen(true);
  };

  const handleAddDeductible = () => {
    setExtraDeductibles([...extraDeductibles, { name: '', amount: 0 }]);
  };

  const handleRemoveDeductible = (index) => {
    setExtraDeductibles(extraDeductibles.filter((_, i) => i !== index));
  };

  const handleDeductibleChange = (index, field, value) => {
    const updated = [...extraDeductibles];
    updated[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setExtraDeductibles(updated);
  };

  const handleSaveExtraDeductibles = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/payroll-periods/${id}/extra-deductibles`, {
        extra_deductibles: extraDeductibles.filter(d => d.name && d.amount),
      });
      toast.success('Extra deductibles saved');
      setExtraDeductiblesDialogOpen(false);
      fetchPeriodData();
    } catch (error) {
      toast.error('Failed to save extra deductibles');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDayEntry = (dayEntryId) => {
    setDeleteTarget({ type: 'day-entry', id: dayEntryId, label: 'day entry' });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    const endpointMap = {
      'worker': `worker-day-entries`,
      'trucker': `trucker-day-entries`,
      'grabman': `grabman-day-entries`,
      'driver': `driver-day-entries`,
      'day-entry': `day-entries`,
    };
    try {
      await axios.delete(`${API_URL}/${endpointMap[type]}/${id}`);
      toast.success(`${deleteTarget.label.charAt(0).toUpperCase() + deleteTarget.label.slice(1)} removed successfully`);
      fetchPeriodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to remove ${deleteTarget.label}`);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  // Toggle functions
  const toggleTruckers = (dayEntryId) => {
    setExpandedTruckers(prev => ({ ...prev, [dayEntryId]: !prev[dayEntryId] }));
  };

  const toggleOwners = (dayEntryId) => {
    setExpandedOwners(prev => ({ ...prev, [dayEntryId]: !prev[dayEntryId] }));
  };

  const toggleWorkers = (dayEntryId) => {
    setExpandedWorkers(prev => ({ ...prev, [dayEntryId]: !prev[dayEntryId] }));
  };

  const toggleSummary = (dayEntryId) => {
    setExpandedSummary(prev => ({ ...prev, [dayEntryId]: !prev[dayEntryId] }));
  };

  // Calculate rate per ton preview
  const calculatedRatePerTon = editDayData?.cane_rate && editDayData?.workers_rate && editDayData?.tons
    ? (parseFloat(editDayData.cane_rate) * parseFloat(editDayData.workers_rate)) / parseFloat(editDayData.tons)
    : 0;

  // Calculate New Day rate per ton preview
  const newDayRatePerTon = newDayData.cane_rate && newDayData.workers_rate && newDayData.tons
    ? (parseFloat(newDayData.cane_rate) * parseFloat(newDayData.workers_rate)) / parseFloat(newDayData.tons)
    : 0;

  // Calculate Weekly Owners Summary with tons_delivered and cane_rate
  const weeklyOwnersSummary = Object.values(dayOwners)
    .flat()
    .filter(o => o)
    .reduce((acc, owner) => {
      if (!acc[owner.owner_id]) {
        acc[owner.owner_id] = {
          owner_id: owner.owner_id,
          owner_name: owner.owner_name,
          deliveries: 0,
          total_tons: 0,
          total_cane_rate: 0,
          total_amount: 0,
        };
      }
      acc[owner.owner_id].deliveries += 1;
      acc[owner.owner_id].total_tons += owner.tons_delivered || 0;
      acc[owner.owner_id].total_cane_rate += owner.cane_rate || 0;
      acc[owner.owner_id].total_amount += owner.amount;
      return acc;
    }, {});

  // Calculate Weekly Truckers Summary with tons_delivered and cane_rate
  const weeklyTruckersSummary = Object.values(dayTruckers)
    .flat()
    .filter(t => t)
    .reduce((acc, trucker) => {
      if (!acc[trucker.trucker_id]) {
        acc[trucker.trucker_id] = {
          trucker_id: trucker.trucker_id,
          trucker_name: trucker.trucker_name,
          deliveries: 0,
          total_tons: 0,
          total_cane_rate: 0,
          total_amount: 0,
        };
      }
      acc[trucker.trucker_id].deliveries += 1;
      acc[trucker.trucker_id].total_tons += trucker.tons_delivered || 0;
      acc[trucker.trucker_id].total_cane_rate += trucker.cane_rate || 0;
      acc[trucker.trucker_id].total_amount += trucker.amount;
      return acc;
    }, {});

  // Filter workers for daily delivery - only cane_cutters and field_personnel
  const dailyDeliveryWorkers = workers.filter(w => 
    w.worker_type === 'cane_cutter' || w.worker_type === 'field_personnel'
  );

  // Filter grabman workers
  const grabmanWorkers = workers.filter(w => w.worker_type === 'grabman');

  // Filter driver workers
  const driverWorkers = workers.filter(w => w.worker_type === 'driver');

  // Get workers already assigned to selected day for filtering - only cane_cutters and field_personnel
  const getAvailableWorkers = () => {
    if (!selectedDayEntry) return dailyDeliveryWorkers;
    const assignedIds = (dayWorkers[selectedDayEntry.id] || []).map(w => w.worker_id);
    const selectedIds = workersList.map(w => w.worker_id).filter(Boolean);
    return dailyDeliveryWorkers.filter(w => !assignedIds.includes(w.id) && !selectedIds.includes(w.id));
  };

  // Print Vouchers
  const handlePrintVouchers = (pageSize) => {
    const fmt = (amount) => {
      return new Intl.NumberFormat('en-BZ', {
        style: 'currency',
        currency: 'BZD',
        minimumFractionDigits: 2,
      }).format(amount || 0);
    };

    // Use grabman/driver summaries from API (SSB calculated on weekly totals)
    const weeklyGrabmenSummary = {};
    if (grabmanSummary?.grabmen) {
      grabmanSummary.grabmen.forEach(gm => {
        weeklyGrabmenSummary[gm.worker_id] = {
          worker_id: gm.worker_id,
          worker_name: gm.worker_name,
          rate: gm.worker_rate,
          total_tons: 0,
          gross_pay: gm.total_amount,
          ssb_employee: gm.ssb_employee,
          has_ssb: true,
        };
      });
    }
    // Fill in total_tons from daily data
    Object.values(dayGrabmen).flat().filter(g => g).forEach(g => {
      if (weeklyGrabmenSummary[g.worker_id]) {
        weeklyGrabmenSummary[g.worker_id].total_tons += g.cane_rate || 0;
        weeklyGrabmenSummary[g.worker_id].rate = g.worker_rate;
      }
    });

    const weeklyDriversSummary = {};
    if (driverSummary?.drivers) {
      driverSummary.drivers.forEach(dr => {
        weeklyDriversSummary[dr.worker_id] = {
          worker_id: dr.worker_id,
          worker_name: dr.worker_name,
          rate: dr.worker_rate,
          total_tons: 0,
          gross_pay: dr.total_amount,
          ssb_employee: dr.ssb_employee,
          has_ssb: true,
        };
      });
    }
    // Fill in total_tons from daily data
    Object.values(dayDrivers).flat().filter(d => d).forEach(d => {
      if (weeklyDriversSummary[d.worker_id]) {
        weeklyDriversSummary[d.worker_id].total_tons += d.cane_rate || 0;
        weeklyDriversSummary[d.worker_id].rate = d.worker_rate;
      }
    });

    // Lookup helpers
    const workersMap = {};
    workers.forEach(w => { workersMap[w.id] = w; });
    const truckersMap = {};
    truckers.forEach(t => { truckersMap[t.id] = t; });
    const ownersMap = {};
    owners.forEach(o => { ownersMap[o.id] = o; });

    const periodDates = `${formatDate(period.start_date)} - ${formatDate(period.end_date)}`;

    const voucherHeight = pageSize === 'legal' ? '4.17in' : '4.75in';
    const vouchersPerPage = pageSize === 'legal' ? 3 : 2;
    const pageHeight = pageSize === 'legal' ? '14in' : '11in';

    const voucherStyle = `
      box-sizing: border-box;
      height: ${voucherHeight};
      padding: 0.3in 0.2in;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      overflow: hidden;
    `;
    const cutLineStyle = 'border-top: 2px dashed #999; margin: 0;';

    const makeVoucherHeader = () => `
      <div style="text-align: center; margin-bottom: 8px;">
        <div style="font-size: 20px; font-weight: bold; letter-spacing: 2px;">CANE PAY</div>
        <div style="font-size: 13px; color: #555;">Cash Voucher</div>
        <div style="font-size: 11px; color: #777; margin-top: 2px;">${period.name} &mdash; ${periodDates}</div>
      </div>
    `;

    const makeRow = (label, value, bold) => `
      <div style="display: flex; justify-content: space-between; padding: 3px 0; font-size: 12px;${bold ? ' font-weight: bold; font-size: 14px;' : ''}">
        <span>${label}</span>
        <span>${value}</span>
      </div>
    `;

    const makeSignature = (name) => `
      <div style="margin-top: auto; padding-top: 10px; text-align: center;">
        <div style="border-top: 1px solid #333; width: 60%; margin: 0 auto; padding-top: 4px; font-size: 11px;">
          ${name}
        </div>
        <div style="font-size: 9px; color: #888; margin-top: 2px;">Signature</div>
      </div>
    `;

    const allVouchers = [];

    // Worker vouchers
    weeklyPayroll.forEach(w => {
      const workerInfo = workersMap[w.worker_id];
      const ssbNum = workerInfo?.ssb_number || 'N/A';
      allVouchers.push(`
        <div style="${voucherStyle}">
          ${makeVoucherHeader()}
          <div>
            ${makeRow('Worker', `<strong>${w.worker_name}</strong>`)}
            ${makeRow('SSB Number', ssbNum)}
            ${makeRow('Total Days Worked', w.total_days)}
            ${makeRow('Total Tons', w.total_tons.toFixed(2))}
            ${makeRow('Gross Pay', fmt(w.gross_pay))}
            ${makeRow('SSB (Employee)', '- ' + fmt(w.ssb_employee))}
            ${makeRow('Net Pay', fmt(w.net_pay), true)}
          </div>
          ${makeSignature(w.worker_name)}
        </div>
      `);
    });

    // Trucker vouchers
    Object.values(weeklyTruckersSummary).forEach(t => {
      const truckerInfo = truckersMap[t.trucker_id];
      const truckNum = truckerInfo?.truck_number || 'N/A';
      const truckerRate = truckerInfo?.rate != null ? truckerInfo.rate + 'x' : 'N/A';
      allVouchers.push(`
        <div style="${voucherStyle}">
          ${makeVoucherHeader()}
          <div>
            ${makeRow('Trucker', `<strong>${t.trucker_name}</strong>`)}
            ${makeRow('Truck Number', truckNum)}
            ${makeRow('Trucker Rate', truckerRate)}
            ${makeRow('Total Deliveries', t.deliveries)}
            ${makeRow('Total Tons Delivered', t.total_tons.toFixed(2))}
            ${makeRow('Total Amount', fmt(t.total_amount), true)}
          </div>
          ${makeSignature(t.trucker_name)}
        </div>
      `);
    });

    // Owner vouchers
    Object.values(weeklyOwnersSummary).forEach(o => {
      const ownerInfo = ownersMap[o.owner_id];
      const idNum = ownerInfo?.id_number || 'N/A';
      const ownerRate = ownerInfo?.rate != null ? ownerInfo.rate + 'x' : 'N/A';
      allVouchers.push(`
        <div style="${voucherStyle}">
          ${makeVoucherHeader()}
          <div>
            ${makeRow('Owner', `<strong>${o.owner_name}</strong>`)}
            ${makeRow('Owner Rate', ownerRate)}
            ${makeRow('ID Number', idNum)}
            ${makeRow('Total Deliveries', o.deliveries)}
            ${makeRow('Total Tons Delivered', o.total_tons.toFixed(2))}
            ${makeRow('Total Amount', fmt(o.total_amount), true)}
          </div>
          ${makeSignature(o.owner_name)}
        </div>
      `);
    });

    // Grabman vouchers
    Object.values(weeklyGrabmenSummary).forEach(g => {
      const workerInfo = workersMap[g.worker_id];
      const ssbNum = workerInfo?.ssb_number || 'N/A';
      const netPay = g.gross_pay - g.ssb_employee;
      allVouchers.push(`
        <div style="${voucherStyle}">
          ${makeVoucherHeader()}
          <div>
            ${makeRow('Grabman', `<strong>${g.worker_name}</strong>`)}
            ${makeRow('SSB Number', ssbNum)}
            ${makeRow('Rate', g.rate + 'x')}
            ${makeRow('Total Tons', g.total_tons.toFixed(2))}
            ${makeRow('Gross Pay', fmt(g.gross_pay))}
            ${makeRow('SSB (Employee)', '- ' + fmt(g.ssb_employee))}
            ${makeRow('Net Pay', fmt(netPay), true)}
          </div>
          ${makeSignature(g.worker_name)}
        </div>
      `);
    });

    // Driver vouchers
    Object.values(weeklyDriversSummary).forEach(d => {
      const workerInfo = workersMap[d.worker_id];
      const ssbNum = workerInfo?.ssb_number || 'N/A';
      const netPay = d.gross_pay - d.ssb_employee;
      allVouchers.push(`
        <div style="${voucherStyle}">
          ${makeVoucherHeader()}
          <div>
            ${makeRow('Driver', `<strong>${d.worker_name}</strong>`)}
            ${makeRow('SSB Number', ssbNum)}
            ${makeRow('Rate', d.rate + 'x')}
            ${makeRow('Total Tons', d.total_tons.toFixed(2))}
            ${makeRow('Gross Pay', fmt(d.gross_pay))}
            ${makeRow('SSB (Employee)', '- ' + fmt(d.ssb_employee))}
            ${makeRow('Net Pay', fmt(netPay), true)}
          </div>
          ${makeSignature(d.worker_name)}
        </div>
      `);
    });

    // Group Leader voucher
    if (groupLeaderSummary) {
      let extraDeductiblesRows = '';
      if (groupLeaderSummary.extra_deductibles_breakdown?.length > 0) {
        groupLeaderSummary.extra_deductibles_breakdown.forEach(d => {
          extraDeductiblesRows += makeRow(`&nbsp;&nbsp;${d.name}`, '- ' + fmt(d.amount));
        });
        extraDeductiblesRows += makeRow('Total Extra Deductibles', '- ' + fmt(groupLeaderSummary.extra_deductibles_total));
      }

      allVouchers.push(`
        <div style="${voucherStyle}">
          ${makeVoucherHeader()}
          <div>
            ${makeRow('', '<strong>Group Leader Summary</strong>')}
            ${makeRow('Owners Total', fmt(groupLeaderSummary.owners_total))}
            ${makeRow('(-) Workers Net Pay', '- ' + fmt(groupLeaderSummary.workers_weekly_total))}
            ${makeRow('(-) Truckers Total', '- ' + fmt(groupLeaderSummary.truckers_total))}
            ${makeRow('(-) Grabman Total', '- ' + fmt(groupLeaderSummary.grabman_total))}
            ${makeRow('(-) Drivers Total', '- ' + fmt(groupLeaderSummary.drivers_total))}
            ${extraDeductiblesRows}
            ${makeRow('(-) Total SSB (Employee + Employer)', '- ' + fmt(groupLeaderSummary.employer_ssb))}
            ${makeRow('Group Leader Pay', fmt(groupLeaderSummary.group_leader_pay), true)}
          </div>
          ${makeSignature('Group Leader')}
        </div>
      `);
    }

    // Build pages with cut lines
    let pagesHtml = '';
    for (let i = 0; i < allVouchers.length; i++) {
      const posOnPage = i % vouchersPerPage;
      if (posOnPage === 0 && i > 0) {
        pagesHtml += '<div style="page-break-before: always;"></div>';
      }
      if (posOnPage > 0) {
        pagesHtml += `<div style="${cutLineStyle}"></div>`;
      }
      pagesHtml += allVouchers[i];
    }

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Cash Vouchers - ${period.name}</title>
  <style>
    @page {
      size: ${pageSize};
      margin: 0.5in;
    }
    * { margin: 0; padding: 0; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #222;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  ${pagesHtml}
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!period) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Payroll period not found</p>
      </div>
    );
  }

  // Group day entries by date
  const entriesByDate = dayEntries.reduce((acc, entry) => {
    if (!acc[entry.date]) {
      acc[entry.date] = [];
    }
    acc[entry.date].push(entry);
    return acc;
  }, {});

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title={period.name}
        subtitle={`${formatDate(period.start_date)} - ${formatDate(period.end_date)}`}
        actions={
          <div className="flex gap-1 sm:gap-2">
            {period.status === 'draft' && (
              <Button
                variant="outline"
                onClick={handleOpenAddDayDialog}
                className="gap-1 sm:gap-2 h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm"
                data-testid="add-daily-delivery-btn"
                title="Add Daily Delivery"
              >
                <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden md:inline">Add Daily Delivery</span>
                <span className="md:hidden">Add</span>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handlePrintVouchers('letter')}
              className="gap-1 sm:gap-2 h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm"
              title="Print Letter"
            >
              <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Letter</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => handlePrintVouchers('legal')}
              className="gap-1 sm:gap-2 h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm"
              title="Print Legal"
            >
              <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Legal</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/payroll')}
              className="gap-1 sm:gap-2 h-8 px-2 sm:h-9 sm:px-3 text-xs sm:text-sm"
              title="Back"
            >
              <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Period Status & Totals */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={getStatusColor(period.status)}>
                    {period.status.charAt(0).toUpperCase() + period.status.slice(1)}
                  </Badge>
                  {statusTransitions[period.status]?.prev && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-xs px-2"
                      onClick={() => { setPendingStatus(statusTransitions[period.status].prev); setStatusDialogOpen(true); }}
                    >
                      {statusTransitions[period.status].prevLabel}
                    </Button>
                  )}
                  {statusTransitions[period.status]?.next && (
                    <Button
                      size="sm"
                      className={`h-6 text-xs px-2 ${period.status === 'paid' ? 'bg-destructive hover:bg-destructive/90' : ''}`}
                      onClick={() => { setPendingStatus(statusTransitions[period.status].next); setStatusDialogOpen(true); }}
                    >
                      {statusTransitions[period.status].nextLabel}
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Workers Rate</p>
                <p className="text-2xl font-bold">{period.workers_rate || 1.0}×</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Days</p>
                <p className="text-2xl font-bold">{dayEntries.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gross Pay</p>
                <p className="text-2xl font-bold">{formatCurrency(period.total_gross)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">SSB (Employee)</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(period.total_ssb_employee)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">SSB (Employer)</p>
                <p className="text-2xl font-bold text-orange-500 dark:text-orange-300">{formatCurrency(period.total_ssb_employer)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total SSB</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatCurrency((period.total_ssb_employee || 0) + (period.total_ssb_employer || 0))}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Pay</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(period.total_net)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Summary and Date views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 overflow-x-auto flex-nowrap h-auto gap-1 w-full justify-start">
            <TabsTrigger value="summary" className="gap-1">
              <DollarSign className="w-4 h-4" />
              Summary
            </TabsTrigger>
            {Object.keys(entriesByDate)
              .sort((a, b) => new Date(a) - new Date(b))
              .map((date) => (
                <TabsTrigger key={date} value={date} className="gap-1">
                  <Calendar className="w-4 h-4" />
                  {(() => { const [y, m, d] = date.split('-'); return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }); })()}
                </TabsTrigger>
              ))}
          </TabsList>

          {/* Summary Tab Content */}
          <TabsContent value="summary" className="space-y-6">
            {/* Weekly Owners Summary - With Tons Delivered and Grabs */}
            {Object.keys(weeklyOwnersSummary).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                    Weekly Owners Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 sm:px-4">Owner</th>
                      <th className="text-right py-2 px-4">Deliveries</th>
                      <th className="text-right py-2 px-4">Grabs Delivered</th>
                      <th className="text-right py-2 px-4">Total Tons</th>
                      <th className="text-right py-2 px-4">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(weeklyOwnersSummary).map((owner) => (
                      <tr key={owner.owner_id} className="border-b">
                        <td className="py-2 px-4 font-medium">{owner.owner_name}</td>
                        <td className="text-right py-2 px-4">{owner.deliveries}</td>
                        <td className="text-right py-2 px-4">{owner.total_tons.toFixed(2)}</td>
                        <td className="text-right py-2 px-4">{formatCaneRate(owner.total_cane_rate)}</td>
                        <td className="text-right py-2 px-4 font-bold text-primary">
                          {formatCurrency(owner.total_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50">
                      <td className="py-3 px-4 font-bold">TOTALS</td>
                      <td className="text-right py-3 px-4 font-bold">
                        {Object.values(weeklyOwnersSummary).reduce((sum, o) => sum + o.deliveries, 0)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {Object.values(weeklyOwnersSummary).reduce((sum, o) => sum + o.total_tons, 0).toFixed(2)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {formatCaneRate(Object.values(weeklyOwnersSummary).reduce((sum, o) => sum + o.total_cane_rate, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-primary">
                        {formatCurrency(
                          Object.values(weeklyOwnersSummary).reduce((sum, o) => sum + o.total_amount, 0)
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Group Leader Summary Card */}
        {groupLeaderSummary && (
          <Card className="border-2 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/30">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  Group Leader Summary
                </div>
                {period.status === 'draft' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenExtraDeductiblesDialog}
                    className="gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    Extra Deductibles
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-muted-foreground">Owners Total</p>
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">{formatCurrency(groupLeaderSummary.owners_total)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-muted-foreground">Workers Net Pay</p>
                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">- {formatCurrency(groupLeaderSummary.workers_weekly_total)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-muted-foreground">Truckers Total</p>
                    <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">- {formatCurrency(groupLeaderSummary.truckers_total)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-muted-foreground">Grabman Total</p>
                    <p className="text-xl font-bold text-orange-600 dark:text-orange-400">- {formatCurrency(groupLeaderSummary.grabman_total)}</p>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-muted-foreground">Drivers Total</p>
                    <p className="text-xl font-bold text-orange-500 dark:text-orange-300">- {formatCurrency(groupLeaderSummary.drivers_total)}</p>
                  </div>
                </div>
                
                {/* Extra Deductibles Breakdown */}
                {groupLeaderSummary.extra_deductibles_breakdown?.length > 0 && (
                  <div className="p-3 bg-background rounded-lg border">
                    <p className="text-muted-foreground mb-2">Extra Deductibles:</p>
                    <div className="space-y-1">
                      {groupLeaderSummary.extra_deductibles_breakdown.map((d, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{d.name}</span>
                          <span className="font-medium text-red-600 dark:text-red-400">- {formatCurrency(d.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm font-bold border-t pt-1">
                        <span>Total Extra Deductibles</span>
                        <span className="text-red-600 dark:text-red-400">- {formatCurrency(groupLeaderSummary.extra_deductibles_total)}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="p-3 bg-background rounded-lg border">
                  <p className="text-muted-foreground">Total SSB (Employee + Employer)</p>
                  <p className="text-xl font-bold text-red-600 dark:text-red-400">- {formatCurrency(groupLeaderSummary.employer_ssb)}</p>
                </div>

                {/* Calculation Breakdown */}
                <div className="p-4 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Calculation:</p>
                  <p className="text-xs font-mono text-purple-800 dark:text-purple-200">{groupLeaderSummary.breakdown?.calculation}</p>
                  <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                    <p className="text-muted-foreground">Group Leader Pay</p>
                    <p className="text-3xl font-bold text-purple-700 dark:text-purple-300">{formatCurrency(groupLeaderSummary.group_leader_pay)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly Worker Payroll - Moved inside Summary tab */}
        {weeklyPayroll.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Weekly Worker Payroll
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 sm:px-4">Worker</th>
                      <th className="text-right py-2 px-2 sm:px-4">Days</th>
                      <th className="text-right py-2 px-2 sm:px-4">Total Tons</th>
                      <th className="text-right py-2 px-4">Gross Pay</th>
                      <th className="text-right py-2 px-4">SSB (Employee)</th>
                      <th className="text-right py-2 px-4">Net Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyPayroll.map((worker) => (
                      <tr key={worker.worker_id} className="border-b">
                        <td className="py-2 px-4">
                          <div>
                            <p className="font-medium">{worker.worker_name}</p>
                            <p className="text-xs text-muted-foreground">{worker.worker_type}</p>
                          </div>
                        </td>
                        <td className="text-right py-2 px-4">{worker.total_days}</td>
                        <td className="text-right py-2 px-4">{worker.total_tons.toFixed(2)}</td>
                        <td className="text-right py-2 px-4">{formatCurrency(worker.gross_pay)}</td>
                        <td className="text-right py-2 px-4 text-orange-600 dark:text-orange-400">
                          {formatCurrency(worker.ssb_employee)}
                        </td>
                        <td className="text-right py-2 px-4 font-bold text-primary">
                          {formatCurrency(worker.net_pay)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50">
                      <td className="py-3 px-4 font-bold">TOTALS</td>
                      <td className="text-right py-3 px-4 font-bold">
                        {weeklyPayroll.reduce((sum, w) => sum + w.total_days, 0)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {weeklyPayroll.reduce((sum, w) => sum + w.total_tons, 0).toFixed(2)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {formatCurrency(weeklyPayroll.reduce((sum, w) => sum + w.gross_pay, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-orange-600 dark:text-orange-400">
                        {formatCurrency(weeklyPayroll.reduce((sum, w) => sum + w.ssb_employee, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-primary">
                        {formatCurrency(weeklyPayroll.reduce((sum, w) => sum + w.net_pay, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly Trucker Summary - Moved inside Summary tab */}
        {Object.keys(weeklyTruckersSummary).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Weekly Trucks Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 sm:px-4">Truck</th>
                      <th className="text-right py-2 px-2 sm:px-4">Deliveries</th>
                      <th className="text-right py-2 px-2 sm:px-4">Grabs Delivered</th>
                      <th className="text-right py-2 px-4">Total Tons</th>
                      <th className="text-right py-2 px-4">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(weeklyTruckersSummary).map((trucker) => (
                      <tr key={trucker.trucker_id} className="border-b">
                        <td className="py-2 px-4 font-medium">{trucker.trucker_name}</td>
                        <td className="text-right py-2 px-4">{trucker.deliveries}</td>
                        <td className="text-right py-2 px-4">{trucker.total_tons.toFixed(2)}</td>
                        <td className="text-right py-2 px-4">{formatCaneRate(trucker.total_cane_rate)}</td>
                        <td className="text-right py-2 px-4 font-bold text-primary">
                          {formatCurrency(trucker.total_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50">
                      <td className="py-3 px-4 font-bold">TOTALS</td>
                      <td className="text-right py-3 px-4 font-bold">
                        {Object.values(weeklyTruckersSummary).reduce((sum, t) => sum + t.deliveries, 0)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {Object.values(weeklyTruckersSummary).reduce((sum, t) => sum + t.total_tons, 0).toFixed(2)}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {formatCaneRate(Object.values(weeklyTruckersSummary).reduce((sum, t) => sum + t.total_cane_rate, 0))}
                      </td>
                      <td className="text-right py-3 px-4 font-bold text-primary">
                        {formatCurrency(
                          Object.values(weeklyTruckersSummary).reduce((sum, t) => sum + t.total_amount, 0)
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly Grabman Summary */}
        {grabmanSummary?.grabmen?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                Weekly Grabman Payroll
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 sm:px-4">Grabman</th>
                      <th className="text-right py-2 px-2 sm:px-4">Days</th>
                      <th className="text-right py-2 px-2 sm:px-4">Gross Pay</th>
                      <th className="text-right py-2 px-2 sm:px-4">SSB (Employee)</th>
                      <th className="text-right py-2 px-2 sm:px-4">Net Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grabmanSummary.grabmen.map((gm) => (
                      <tr key={gm.worker_id} className="border-b">
                        <td className="py-2 px-2 sm:px-4 font-medium">{gm.worker_name}</td>
                        <td className="text-right py-2 px-2 sm:px-4">{gm.entries}</td>
                        <td className="text-right py-2 px-2 sm:px-4">{formatCurrency(gm.total_amount)}</td>
                        <td className="text-right py-2 px-2 sm:px-4 text-orange-600 dark:text-orange-400">
                          {formatCurrency(gm.ssb_employee)}
                        </td>
                        <td className="text-right py-2 px-2 sm:px-4 font-bold text-primary">
                          {formatCurrency(gm.net_pay)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50">
                      <td className="py-3 px-2 sm:px-4 font-bold">TOTALS</td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold">
                        {grabmanSummary.grabmen.reduce((sum, gm) => sum + gm.entries, 0)}
                      </td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold">
                        {formatCurrency(grabmanSummary.grabmen.reduce((sum, gm) => sum + gm.total_amount, 0))}
                      </td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold text-orange-600 dark:text-orange-400">
                        {formatCurrency(grabmanSummary.grabmen.reduce((sum, gm) => sum + gm.ssb_employee, 0))}
                      </td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold text-primary">
                        {formatCurrency(grabmanSummary.grabmen.reduce((sum, gm) => sum + gm.net_pay, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly Driver Summary */}
        {driverSummary?.drivers?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Weekly Driver Payroll
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 sm:px-4">Driver</th>
                      <th className="text-right py-2 px-2 sm:px-4">Days</th>
                      <th className="text-right py-2 px-2 sm:px-4">Gross Pay</th>
                      <th className="text-right py-2 px-2 sm:px-4">SSB (Employee)</th>
                      <th className="text-right py-2 px-2 sm:px-4">Net Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driverSummary.drivers.map((dr) => (
                      <tr key={dr.worker_id} className="border-b">
                        <td className="py-2 px-2 sm:px-4 font-medium">{dr.worker_name}</td>
                        <td className="text-right py-2 px-2 sm:px-4">{dr.entries}</td>
                        <td className="text-right py-2 px-2 sm:px-4">{formatCurrency(dr.total_amount)}</td>
                        <td className="text-right py-2 px-2 sm:px-4 text-blue-600 dark:text-blue-400">
                          {formatCurrency(dr.ssb_employee)}
                        </td>
                        <td className="text-right py-2 px-2 sm:px-4 font-bold text-primary">
                          {formatCurrency(dr.net_pay)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-muted/50">
                      <td className="py-3 px-2 sm:px-4 font-bold">TOTALS</td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold">
                        {driverSummary.drivers.reduce((sum, dr) => sum + dr.entries, 0)}
                      </td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold">
                        {formatCurrency(driverSummary.drivers.reduce((sum, dr) => sum + dr.total_amount, 0))}
                      </td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold text-blue-600 dark:text-blue-400">
                        {formatCurrency(driverSummary.drivers.reduce((sum, dr) => sum + dr.ssb_employee, 0))}
                      </td>
                      <td className="text-right py-3 px-2 sm:px-4 font-bold text-primary">
                        {formatCurrency(driverSummary.drivers.reduce((sum, dr) => sum + dr.net_pay, 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
          </TabsContent>

          {/* Date Tab Contents */}
          {Object.keys(entriesByDate).length === 0 ? (
            <TabsContent value="summary" className="mt-4">
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">
                    No day entries yet. Click &quot;Add Daily Delivery&quot; to create one.
                  </p>
                  {period.status === 'draft' && (
                    <Button onClick={handleOpenAddDayDialog} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Daily Delivery
                    </Button>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ) : (
            Object.entries(entriesByDate)
              .sort(([a], [b]) => new Date(a) - new Date(b))
              .map(([date, entries]) => (
                <TabsContent key={date} value={date} className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          {formatDate(date)}
                        </div>
                        <Badge variant="secondary">
                          {entries.length} {entries.length === 1 ? 'Delivery' : 'Deliveries'}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                    {entries.map((entry, index) => (
                      <div key={entry.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <UserCircle className="w-4 h-4 text-purple-500" />
                            <span className="font-medium">
                              {dayOwners[entry.id]?.[0]?.owner_name || `Delivery ${index + 1}`}
                            </span>
                            {entry.notes && (
                              <span className="text-xs text-muted-foreground">
                                ({entry.notes})
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 sm:gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenTruckerDialog(entry)}
                              disabled={period.status !== 'draft'}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Trucker</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenWorkerDialog(entry)}
                              disabled={period.status !== 'draft'}
                              data-testid={`add-workers-btn-${entry.id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Workers</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEditDayDialog(entry)}
                              disabled={period.status !== 'draft'}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() => handleDeleteDayEntry(entry.id)}
                              disabled={period.status !== 'draft'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Rates Display - Now includes Workers Rate */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Tons</p>
                            <p className="font-semibold">{formatCaneRate(entry.cane_rate)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Workers Rate</p>
                            <p className="font-semibold text-blue-600">{entry.workers_rate}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Total Grabs</p>
                            <p className="font-semibold">{entry.tons}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Rate/Ton</p>
                            <p className="font-semibold text-primary">{formatTonRate(entry.rate_per_ton)}</p>
                          </div>
                        </div>

                        {/* Owners Section (Read Only - set during period creation) */}
                        <div className="border-t pt-3">
                          <button
                            onClick={() => toggleOwners(entry.id)}
                            className="flex items-center justify-between w-full text-sm hover:text-primary transition-colors"
                          >
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <UserCircle className="w-4 h-4" />
                              <span>{dayOwners[entry.id]?.length || 0} owners (set at period creation)</span>
                            </div>
                            {expandedOwners[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {expandedOwners[entry.id] && dayOwners[entry.id]?.length > 0 && (
                            <div className="mt-3 bg-muted/30 rounded-lg p-3">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-border">
                                    <th className="text-left py-2">Owner</th>
                                    <th className="text-right py-2">Rate</th>
                                    <th className="text-right py-2">Tons</th>
                                    <th className="text-right py-2">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dayOwners[entry.id].map((owner) => (
                                    <tr key={owner.id} className="border-b border-border/50 last:border-0">
                                      <td className="py-2 font-medium">{owner.owner_name}</td>
                                      <td className="text-right py-2">{owner.owner_rate}×</td>
                                      <td className="text-right py-2">{(owner.tons_delivered || 0).toFixed(2)}</td>
                                      <td className="text-right py-2 font-semibold text-primary">
                                        {formatCurrency(owner.amount)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* Truckers Section */}
                        <div className="border-t pt-3">
                          <button
                            onClick={() => toggleTruckers(entry.id)}
                            className="flex items-center justify-between w-full text-sm hover:text-primary transition-colors"
                          >
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Truck className="w-4 h-4" />
                              <span>{dayTruckers[entry.id]?.length || 0} trucks assigned</span>
                            </div>
                            {expandedTruckers[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {expandedTruckers[entry.id] && (
                            <div className="mt-3 space-y-2">
                              {dayTruckers[entry.id]?.length > 0 ? (
                                <div className="bg-muted/30 rounded-lg p-3">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-border">
                                        <th className="text-left py-2">Trucker</th>
                                        <th className="text-right py-2">Rate</th>
                                        <th className="text-right py-2">Tons</th>
                                        <th className="text-right py-2">Amount</th>
                                        {period.status === 'draft' && <th className="text-right py-2">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dayTruckers[entry.id].map((trucker) => (
                                        <tr key={trucker.id} className="border-b border-border/50 last:border-0">
                                          <td className="py-2 font-medium">{trucker.trucker_name}</td>
                                          <td className="text-right py-2">{trucker.trucker_rate}×</td>
                                          <td className="text-right py-2">{(trucker.tons_delivered || 0).toFixed(2)}</td>
                                          <td className="text-right py-2 font-semibold text-primary">
                                            {formatCurrency(trucker.amount)}
                                          </td>
                                          {period.status === 'draft' && (
                                            <td className="text-right py-2">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 text-destructive"
                                                onClick={() => handleDeleteTrucker(entry.id, trucker.id)}
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-2">No truckers assigned yet</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Workers Section */}
                        <div className="border-t pt-3">
                          <button
                            onClick={() => toggleWorkers(entry.id)}
                            className="flex items-center justify-between w-full text-sm hover:text-primary transition-colors"
                          >
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4" />
                              <span>{entry.worker_count} workers assigned</span>
                            </div>
                            {expandedWorkers[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {expandedWorkers[entry.id] && (
                            <div className="mt-3 space-y-2">
                              {dayWorkers[entry.id]?.length > 0 ? (
                                <div className="bg-muted/30 rounded-lg p-3">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-border">
                                        <th className="text-left py-2">Worker</th>
                                        <th className="text-right py-2">Grabs Cut</th>
                                        <th className="text-right py-2">Daily Pay</th>
                                        {period.status === 'draft' && <th className="text-right py-2">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dayWorkers[entry.id].map((worker) => (
                                        <tr key={worker.id} className="border-b border-border/50 last:border-0">
                                          <td className="py-2 font-medium">{worker.worker_name}</td>
                                          <td className="text-right py-2">{worker.tons_cut.toFixed(2)}</td>
                                          <td className="text-right py-2 font-semibold text-primary">
                                            {formatCurrency(worker.daily_pay)}
                                          </td>
                                          {period.status === 'draft' && (
                                            <td className="text-right py-2">
                                              <div className="flex items-center justify-end gap-1">
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-7 w-7 p-0"
                                                  onClick={() => handleOpenWorkerDialog(entry, worker)}
                                                >
                                                  <Edit className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-7 w-7 p-0 text-destructive"
                                                  onClick={() => handleDeleteWorker(entry.id, worker.id)}
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-2">No workers assigned yet</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Grabman Section */}
                        <div className="border-t pt-3">
                          <button
                            onClick={() => setExpandedGrabmen(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                            className="flex items-center justify-between w-full text-sm hover:text-primary transition-colors"
                          >
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4 text-orange-500" />
                              <span>{dayGrabmen[entry.id]?.length || 0} grabmen assigned</span>
                            </div>
                            {expandedGrabmen[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {expandedGrabmen[entry.id] && (
                            <div className="mt-3 space-y-2">
                              {dayGrabmen[entry.id]?.length > 0 ? (
                                <div className="bg-orange-50 dark:bg-orange-950/30 rounded-lg p-3">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-orange-200 dark:border-orange-800">
                                        <th className="text-left py-2">Grabman</th>
                                        <th className="text-right py-2">Rate</th>
                                        <th className="text-right py-2">Tons</th>
                                        <th className="text-right py-2">Amount</th>
                                        {period.status === 'draft' && <th className="text-right py-2">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dayGrabmen[entry.id].map((grabman) => (
                                        <tr key={grabman.id} className="border-b border-orange-100 dark:border-orange-900 last:border-0">
                                          <td className="py-2 font-medium">{grabman.worker_name}</td>
                                          <td className="text-right py-2">{grabman.worker_rate}×</td>
                                          <td className="text-right py-2">{formatCaneRate(grabman.cane_rate)}</td>
                                          <td className="text-right py-2 font-semibold text-orange-600 dark:text-orange-400">
                                            {formatCurrency(grabman.amount)}
                                          </td>
                                          {period.status === 'draft' && (
                                            <td className="text-right py-2">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 text-destructive"
                                                onClick={() => handleDeleteGrabman(grabman.id)}
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-2">No grabmen assigned yet</p>
                              )}
                              {period.status === 'draft' && grabmanWorkers.length > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenGrabmanDialog(entry)}
                                  className="w-full gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Grabman
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Driver Section (Group Leader's Drivers) */}
                        <div className="border-t pt-3">
                          <button
                            onClick={() => setExpandedDrivers(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                            className="flex items-center justify-between w-full text-sm hover:text-primary transition-colors"
                          >
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="w-4 h-4 text-blue-500" />
                              <span>{dayDrivers[entry.id]?.length || 0} drivers assigned (Group Leader)</span>
                            </div>
                            {expandedDrivers[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {expandedDrivers[entry.id] && (
                            <div className="mt-3 space-y-2">
                              {dayDrivers[entry.id]?.length > 0 ? (
                                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-blue-200 dark:border-blue-800">
                                        <th className="text-left py-2">Driver</th>
                                        <th className="text-right py-2">Rate</th>
                                        <th className="text-right py-2">Tons</th>
                                        <th className="text-right py-2">Amount</th>
                                        {period.status === 'draft' && <th className="text-right py-2">Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dayDrivers[entry.id].map((driver) => (
                                        <tr key={driver.id} className="border-b border-blue-100 dark:border-blue-900 last:border-0">
                                          <td className="py-2 font-medium">{driver.worker_name}</td>
                                          <td className="text-right py-2">{driver.worker_rate}×</td>
                                          <td className="text-right py-2">{formatCaneRate(driver.cane_rate)}</td>
                                          <td className="text-right py-2 font-semibold text-blue-600 dark:text-blue-400">
                                            {formatCurrency(driver.amount)}
                                          </td>
                                          {period.status === 'draft' && (
                                            <td className="text-right py-2">
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 text-destructive"
                                                onClick={() => handleDeleteDriver(driver.id)}
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </Button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground text-center py-2">No drivers assigned yet (optional)</p>
                              )}
                              {period.status === 'draft' && driverWorkers.length > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenDriverDialog(entry)}
                                  className="w-full gap-1"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Driver
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Daily Summary */}
                        <div className="border-t pt-3">
                          <button
                            onClick={() => toggleSummary(entry.id)}
                            className="flex items-center justify-between w-full text-sm hover:text-primary transition-colors"
                          >
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <DollarSign className="w-4 h-4" />
                              <span>Daily Distribution Summary</span>
                            </div>
                            {expandedSummary[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {expandedSummary[entry.id] && (
                            <div className="mt-3 bg-primary/5 rounded-lg p-4">
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 text-xs sm:text-sm">
                                <div>
                                  <p className="text-muted-foreground">Total Tons</p>
                                  <p className="font-bold text-lg">{formatCaneRate(entry.cane_rate)}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Total Daily Amount</p>
                                  <p className="font-bold text-lg text-primary">
                                    {formatCurrency(entry.cane_rate * (period.workers_rate || 1))}
                                  </p>
                                  <p className="text-xs text-muted-foreground">Tons × Workers Rate</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Owner Amount</p>
                                  <p className="font-semibold text-lg text-purple-600">
                                    {formatCurrency((dayOwners[entry.id] || []).reduce((sum, o) => sum + o.amount, 0))}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Trucker Amount</p>
                                  <p className="font-semibold text-lg text-blue-600">
                                    {formatCurrency((dayTruckers[entry.id] || []).reduce((sum, t) => sum + t.amount, 0))}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Grabman Amount</p>
                                  <p className="font-semibold text-lg text-orange-600">
                                    {formatCurrency((dayGrabmen[entry.id] || []).reduce((sum, g) => sum + g.amount, 0))}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Workers Tons</p>
                                  {(() => {
                                    const workerTons = (dayWorkers[entry.id] || []).reduce((sum, w) => sum + w.tons_cut, 0);
                                    const matches = Math.abs(workerTons - entry.tons) < 0.001;
                                    return (
                                      <p className={`font-semibold text-lg ${matches ? 'text-green-600' : 'text-red-600'}`}>
                                        {workerTons.toFixed(2)} / {entry.tons}
                                      </p>
                                    );
                                  })()}
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Workers Pool</p>
                                  <p className="font-semibold text-lg text-green-600">
                                    {formatCurrency((dayWorkers[entry.id] || []).reduce((sum, w) => sum + w.daily_pay, 0))}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
              ))
          )}
        </Tabs>
      </div>

      {/* Edit Day Entry Dialog (workers_rate and tons only) */}
      <Dialog open={editDayDialogOpen} onOpenChange={setEditDayDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Day Entry</DialogTitle>
            <DialogDescription>
              {editDayData && `${formatDate(editDayData.date)} - Tons: ${formatCaneRate(editDayData.cane_rate)}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDayEntry}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="workers_rate">Workers Rate (Multiplier) *</Label>
                <Input
                  id="workers_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editDayData?.workers_rate || ''}
                  onChange={(e) => setEditDayData({ ...editDayData, workers_rate: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tons">Total Grabs Delivered *</Label>
                <Input
                  id="tons"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editDayData?.tons || ''}
                  onChange={(e) => setEditDayData({ ...editDayData, tons: e.target.value })}
                  required
                />
              </div>

              {calculatedRatePerTon > 0 && (
                <div className="p-4 bg-accent/10 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">New Rate per Ton:</span>
                    <span className="text-lg font-bold text-primary">
                      {formatCaneRate(calculatedRatePerTon)}/ton
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDayDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Daily Delivery Dialog */}
      <Dialog open={addDayDialogOpen} onOpenChange={setAddDayDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Daily Delivery</DialogTitle>
            <DialogDescription>
              Create a new daily delivery for a missed day
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDayEntry}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new_date">Date *</Label>
                <Input
                  id="new_date"
                  type="date"
                  value={newDayData.date}
                  onChange={(e) => setNewDayData({ ...newDayData, date: e.target.value })}
                  min={period?.start_date}
                  max={period?.end_date}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_owner">Owner (Optional)</Label>
                <Select
                  value={newDayData.owner_id}
                  onValueChange={(value) => setNewDayData({ ...newDayData, owner_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select owner (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No owner</SelectItem>
                    {owners.map((owner) => (
                      <SelectItem key={owner.id} value={owner.id}>
                        {owner.name} (Rate: {owner.rate}×)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_cane_rate">Tons *</Label>
                <Input
                  id="new_cane_rate"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={newDayData.cane_rate}
                  onChange={(e) => setNewDayData({ ...newDayData, cane_rate: e.target.value })}
                  placeholder="39.8594"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_tons">Tons Delivered *</Label>
                  <Input
                    id="new_tons"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newDayData.tons}
                    onChange={(e) => setNewDayData({ ...newDayData, tons: e.target.value })}
                    placeholder="1"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_workers_rate">Workers Rate *</Label>
                  <Input
                    id="new_workers_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newDayData.workers_rate}
                    onChange={(e) => setNewDayData({ ...newDayData, workers_rate: e.target.value })}
                    placeholder="1"
                    required
                  />
                </div>
              </div>

              {newDayRatePerTon > 0 && (
                <div className="p-4 bg-accent/10 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Rate per Ton:</span>
                    <span className="text-lg font-bold text-primary">
                      {formatCaneRate(newDayRatePerTon)}/ton
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDayDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Delivery
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Workers Dialog - Now supports multiple workers */}
      <Dialog open={workerDialogOpen} onOpenChange={setWorkerDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditingWorker ? 'Edit Worker' : 'Add Workers'} - {selectedDayEntry && formatDate(selectedDayEntry.date)}
            </DialogTitle>
            <DialogDescription>
              Rate: {selectedDayEntry && formatCaneRate(selectedDayEntry.rate_per_ton)}/ton
              {!isEditingWorker && ' • Add multiple workers at once'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveWorkers}>
            <div className="space-y-4 py-4">
              {isEditingWorker ? (
                // Single worker edit mode
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Worker</Label>
                    <Input 
                      value={workersList[0]?.worker_id ? workers.find(w => w.id === workersList[0].worker_id)?.name : ''} 
                      disabled 
                      className="bg-muted" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tons_cut">Grabs Cut *</Label>
                    <Input
                      id="tons_cut"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={workersList[0]?.tons_cut || ''}
                      onChange={(e) => handleWorkerChange(0, 'tons_cut', e.target.value)}
                      placeholder="5.5"
                      required
                    />
                  </div>
                  {workersList[0]?.tons_cut && selectedDayEntry && (
                    <div className="p-4 bg-accent/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Daily Pay:</span>
                        <span className="text-lg font-bold text-primary">
                          {formatCurrency(parseFloat(workersList[0].tons_cut) * selectedDayEntry.rate_per_ton)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Multiple workers add mode
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Workers</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAddWorkerRow}
                      className="h-7 gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add More
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1">
                      <span className="col-span-6">Worker</span>
                      <span className="col-span-3">Grabs Cut</span>
                      <span className="col-span-2">Pay</span>
                      <span className="col-span-1"></span>
                    </div>
                    
                    {workersList.map((workerItem, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-6">
                          <Select
                            value={workerItem.worker_id}
                            onValueChange={(value) => handleWorkerChange(index, 'worker_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select worker" />
                            </SelectTrigger>
                            <SelectContent>
                              {getAvailableWorkers().map((worker) => (
                                <SelectItem key={worker.id} value={worker.id}>
                                  {worker.name}
                                </SelectItem>
                              ))}
                              {workerItem.worker_id && (
                                <SelectItem value={workerItem.worker_id}>
                                  {workers.find(w => w.id === workerItem.worker_id)?.name}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={workerItem.tons_cut}
                            onChange={(e) => handleWorkerChange(index, 'tons_cut', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-span-2 text-sm font-medium text-primary">
                          {workerItem.tons_cut && selectedDayEntry
                            ? formatCurrency(parseFloat(workerItem.tons_cut) * selectedDayEntry.rate_per_ton)
                            : '-'}
                        </div>
                        <div className="col-span-1">
                          {workersList.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-9 w-9 p-0 text-destructive"
                              onClick={() => handleRemoveWorkerRow(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total preview */}
                  {workersList.some(w => w.worker_id && w.tons_cut) && selectedDayEntry && (
                    <div className="p-4 bg-accent/10 rounded-lg mt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          Total ({workersList.filter(w => w.worker_id && w.tons_cut).length} workers):
                        </span>
                        <span className="text-lg font-bold text-primary">
                          {formatCurrency(
                            workersList
                              .filter(w => w.worker_id && w.tons_cut)
                              .reduce((sum, w) => sum + (parseFloat(w.tons_cut) * selectedDayEntry.rate_per_ton), 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWorkerDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {isEditingWorker ? 'Update' : `Add ${workersList.filter(w => w.worker_id && w.tons_cut).length} Worker(s)`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Trucker Dialog */}
      <Dialog open={truckerDialogOpen} onOpenChange={setTruckerDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Add Trucker - {selectedDayForTrucker && formatDate(selectedDayForTrucker.date)}
            </DialogTitle>
            <DialogDescription>
              Tons: {selectedDayForTrucker && formatCaneRate(selectedDayForTrucker.cane_rate)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTrucker}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="trucker_id">Trucker *</Label>
                <Select
                  value={truckerFormData.trucker_id}
                  onValueChange={(value) => setTruckerFormData({ ...truckerFormData, trucker_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select trucker" />
                  </SelectTrigger>
                  <SelectContent>
                    {truckers.map((trucker) => (
                      <SelectItem key={trucker.id} value={trucker.id}>
                        {trucker.name} (Rate: {trucker.rate}×)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {truckerFormData.trucker_id && selectedDayForTrucker && (() => {
                const selectedTrucker = truckers.find(t => t.id === truckerFormData.trucker_id);
                return selectedTrucker && (
                  <div className="p-4 bg-accent/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Trucker Amount:</span>
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(selectedTrucker.rate * selectedDayForTrucker.cane_rate)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
                      <span>Tons Delivered:</span>
                      <span>{selectedDayForTrucker.tons}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTruckerDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Add Trucker
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Grabman Dialog */}
      <Dialog open={grabmanDialogOpen} onOpenChange={setGrabmanDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Add Grabman - {selectedDayForGrabman && formatDate(selectedDayForGrabman.date)}
            </DialogTitle>
            <DialogDescription>
              Select a grabman to add to this daily delivery. Pay = Rate × Tons
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddGrabman}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Grabman *</Label>
                <Select
                  value={grabmanFormData.worker_id}
                  onValueChange={(value) => setGrabmanFormData({ ...grabmanFormData, worker_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select grabman" />
                  </SelectTrigger>
                  <SelectContent>
                    {grabmanWorkers.map((worker) => (
                      <SelectItem key={worker.id} value={worker.id}>
                        {worker.name} (Rate: {worker.rate}×)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={grabmanFormData.notes}
                  onChange={(e) => setGrabmanFormData({ ...grabmanFormData, notes: e.target.value })}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGrabmanDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !grabmanFormData.worker_id}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Add Grabman
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Driver Dialog */}
      <Dialog open={driverDialogOpen} onOpenChange={setDriverDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Add Driver - {selectedDayForDriver && formatDate(selectedDayForDriver.date)}
            </DialogTitle>
            <DialogDescription>
              Select a driver (for group leader) to add to this daily delivery. Pay = Rate × Tons
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddDriver}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Driver *</Label>
                <Select
                  value={driverFormData.worker_id}
                  onValueChange={(value) => setDriverFormData({ ...driverFormData, worker_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {driverWorkers.map((worker) => (
                      <SelectItem key={worker.id} value={worker.id}>
                        {worker.name} (Rate: {worker.rate}×)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={driverFormData.notes}
                  onChange={(e) => setDriverFormData({ ...driverFormData, notes: e.target.value })}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDriverDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !driverFormData.worker_id}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Add Driver
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Extra Deductibles Dialog */}
      <Dialog open={extraDeductiblesDialogOpen} onOpenChange={setExtraDeductiblesDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Extra Deductibles</DialogTitle>
            <DialogDescription>
              Add extra deductions for the group leader (e.g., pay for captain, transport costs)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {extraDeductibles.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No extra deductibles added yet</p>
            ) : (
              <div className="space-y-2">
                {extraDeductibles.map((deductible, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      placeholder="Name (e.g., Captain Pay)"
                      value={deductible.name}
                      onChange={(e) => handleDeductibleChange(index, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={deductible.amount || ''}
                      onChange={(e) => handleDeductibleChange(index, 'amount', e.target.value)}
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => handleRemoveDeductible(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handleAddDeductible}
              className="w-full gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Deductible
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setExtraDeductiblesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveExtraDeductibles} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Deductibles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.label}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this {deleteTarget?.label}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Dialog */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change Status to "{pendingStatus?.charAt(0).toUpperCase()}{pendingStatus?.slice(1)}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusTransitions[period?.status]?.next === pendingStatus
                ? statusTransitions[period?.status]?.nextDesc
                : `Move this payroll back to ${pendingStatus}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={changingStatus}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStatusChange}
              disabled={changingStatus}
              className={pendingStatus === 'reversed' ? 'bg-destructive hover:bg-destructive/90' : ''}
            >
              {changingStatus ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
